import React from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { dashboardApi } from '@/shared/api';
import type { DashboardStats, StatCard, QuickAction } from '@/shared/types';

/**
 * Страница панели управления
 * Отображает статистику и быстрые действия для администратора
 */
export const DashboardPage: React.FC = () => {
  // Загрузка статистики с помощью SWR
  const {
    data: statsResponse,
    error,
    isLoading
  } = useSWR('dashboard-stats', dashboardApi.getStats, {
    refreshInterval: 30000 // Обновляем каждые 30 секунд
  });

  const stats = statsResponse?.stats;

  /**
   * Конфигурация статистических карточек
   */
  const getStatCards = (stats: DashboardStats): StatCard[] => [
    {
      title: 'Врачи',
      value: stats.doctorsCount,
      icon: 'fas fa-user-md',
      color: 'primary'
    },
    {
      title: 'Категории цен',
      value: stats.priceCategoriesCount,
      icon: 'fas fa-list',
      color: 'secondary'
    },
    {
      title: 'Услуги',
      value: stats.priceItemsCount,
      icon: 'fas fa-tags',
      color: 'success'
    }
  ];

  /**
   * Быстрые действия
   */
  const quickActions: QuickAction[] = [
    {
      title: 'Управление врачами',
      description: 'Добавление, редактирование и управление врачами',
      icon: 'fas fa-user-md',
      href: '/doctors',
      color: 'primary'
    },
    {
      title: 'Управление ценами',
      description: 'Настройка цен и услуг клиники',
      icon: 'fas fa-tags',
      href: '/prices',
      color: 'secondary'
    }
  ];

  /**
   * Компонент статистической карточки с DaisyUI
   */
  const StatCardComponent: React.FC<{ card: StatCard }> = ({ card }) => (
    <div className="stat bg-base-100 rounded-lg shadow-sm border border-base-300">
      <div className={`stat-figure text-${card.color}`}>
        <i className={`${card.icon} text-2xl`}></i>
      </div>
      <div className="stat-title text-base-content/70">{card.title}</div>
      <div className="stat-value text-base-content">{card.value}</div>
    </div>
  );

  /**
   * Компонент быстрого действия с DaisyUI
   */
  const QuickActionComponent: React.FC<{ action: QuickAction }> = ({
    action
  }) => (
    <Link
      to={action.href}
      className="card card-bordered bg-base-100 shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      <div className="card-body text-center p-2">
        <div
          className={`w-12 h-12 bg-${action.color} rounded-lg mx-auto flex items-center justify-center`}
        >
          <i
            className={`${action.icon} text-${action.color}-content text-lg`}
          ></i>
        </div>
        <h3 className="card-title text-base-content justify-center">
          {action.title}
        </h3>
        <p className="text-base-content/70 text-sm">{action.description}</p>
      </div>
    </Link>
  );

  return (
    <Layout title="Панель управления">
      <div className="space-y-6">
        {/* Приветствие */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center">
                <i className="fas fa-chart-line text-2xl text-primary-content"></i>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-base-content">
                  Добро пожаловать в админ-панель!
                </h2>
                <p className="text-base-content/70">
                  Управляйте содержимым сайта педиатрической клиники "Алёнушка"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Статистика */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center">
              <i className="fas fa-chart-bar text-secondary-content text-sm"></i>
            </div>
            <h2 className="text-xl font-bold text-base-content">Статистика</h2>
          </div>

          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" text="Загрузка статистики..." />
            </div>
          )}

          {error && (
            <ErrorMessage
              message="Ошибка загрузки статистики. Попробуйте обновить страницу."
              className="mb-6"
            />
          )}

          {stats && (
            <div className="stats stats-vertical lg:stats-horizontal shadow-sm w-full gap-4">
              {getStatCards(stats).map((card, index) => (
                <StatCardComponent key={index} card={card} />
              ))}
            </div>
          )}
        </div>

        {/* Быстрые действия */}
        <div>
          <h2 className="text-xl font-bold text-base-content mb-6">
            Быстрые действия
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickActions.map((action, index) => (
              <QuickActionComponent key={index} action={action} />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};
